import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { saveAuthData } from "../../utils/auth";

const initialState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  casinoConversionRate: null,
};

export const loginAsync = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const formData = new URLSearchParams();
      formData.append("user_name", username);
      formData.append("password", password);
      formData.append("grant_type", "password");

      const response = await axiosInstance.post("/user/userLogin", formData, {
        headers: {
          "Content-type": "application/x-www-form-urlencoded",
          Authorization: "Basic YXBwbGljYXRpb246c2VjcmV0",
        },
      });
      saveAuthData(response);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

export const demoLoginAsync = createAsyncThunk(
  "auth/demoLogin",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const demoResponse = await axiosInstance.post(
        "/user/autoDemoUserLogin",
        {},
        {},
      );

      if (!demoResponse?.data?.user_name || !demoResponse?.data?.password) {
        return rejectWithValue("Failed to get demo credentials");
      }

      const result = await dispatch(
        loginAsync({
          username: demoResponse.data.user_name,
          password: demoResponse.data.password,
        }),
      );
      if (loginAsync.fulfilled.match(result)) {
        return result.payload;
      } else {
        return rejectWithValue(result.payload || "Demo login failed");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Demo login failed",
      );
    }
  },
);

export const checkPhoneNumber = async (phoneNumber) => {
  const response = await axiosInstance.get(`/account/v2/users/phones/${phoneNumber}/:exists`, {
    headers: {
      authorization: "Basic YXBwbGljYXRpb246c2VjcmV0",
      "Content-Type": "application/json",
      accept: "application/json",
    },
  });
  return response;
};

export const fetchCasinoConversionRateAsync = createAsyncThunk(
  "auth/fetchCasinoConversionRate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/getCasinoConversionRate");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch casino conversion rate",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthFromStorage: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token?.accessToken || "";
        state.user = action.payload.data || null;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(demoLoginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(demoLoginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token?.accessToken || "demo_authenticated";
        state.user = action.payload.data || null;
        state.error = null;
      })
      .addCase(demoLoginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCasinoConversionRateAsync.fulfilled, (state, action) => {
        state.casinoConversionRate = action.payload;
      })
      .addCase(fetchCasinoConversionRateAsync.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { logout, setAuthFromStorage } = authSlice.actions;
export default authSlice.reducer;
